/**
 * Pending Approval Page Component
 * Shows all markets awaiting approval
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../store/AuthContext';
import { apiService } from '../services/apiService';
import MarketDetailModal from '../components/modals/MarketDetailModal';
import './PendingApprovalPage.css';

interface Market {
  id: string;
  headline: string;
  description: string;
  category: string;
  answer_type: string;
  answer_options: string[] | null;
  min_stake: number;
  max_stake: number;
  end_time_type: string;
  duration_value: number | null;
  duration_unit: string | null;
  end_date: string | null;
  payout_type: string;
  payout_after: string | null;
  payout_date: string | null;
  image_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  settled_at: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
}

interface Approval {
  id: string;
  admin_id: string;
  approved: boolean;
  approved_at: string | null;
  admin_name?: string;
  admin_email?: string;
}

const PendingApprovalPage: React.FC = () => {
  const { admin } = useAuth();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [approvals, setApprovals] = useState<Record<string, Approval[]>>({});
  const [approving, setApproving] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadPendingMarkets();
    // Track when this page is opened (set timestamp on mount)
    localStorage.setItem('last_opened_pending-approval', new Date().toISOString());
  }, []);

  // Load approvals for all markets when they load
  useEffect(() => {
    if (markets.length > 0) {
      markets.forEach(market => {
        if (market.id) {
          loadApprovals(market.id);
        }
      });
    }
  }, [markets]);

  useEffect(() => {
    if (selectedMarket) {
      loadApprovals(selectedMarket.id);
    }
  }, [selectedMarket]);

  const loadPendingMarkets = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading pending markets...');
      const response = await apiService.getPendingMarkets();
      console.log('Pending markets response:', response);
      console.log('Markets count:', response?.markets?.length || 0);
      
      if (response && response.markets) {
        // Debug: Log the actual market structure
        console.log('First market structure:', response.markets[0]);
        console.log('First market keys:', response.markets[0] ? Object.keys(response.markets[0]) : 'no markets');
        console.log('First market id:', response.markets[0]?.id);
        console.log('First market id type:', typeof response.markets[0]?.id);
        
        setMarkets(response.markets);
        
        // Mark all markets as viewed when the screen is opened
        if (response.markets.length > 0) {
          const marketIds = response.markets.map(m => {
            const id = m?.id || m?.Id || m?.ID || m?.market_id || m?.marketId;
            console.log('Market id candidates:', { id: m?.id, Id: m?.Id, ID: m?.ID, market_id: m?.market_id, marketId: m?.marketId, allKeys: Object.keys(m || {}) });
            return id;
          }).filter(id => id);
          console.log('Marking markets as viewed:', marketIds);
          if (marketIds.length > 0) {
            try {
              await apiService.markMarketsAsViewed(marketIds);
            } catch (err) {
              console.error('Failed to mark markets as viewed:', err);
            }
          }
        }
      } else {
        console.warn('No markets in response:', response);
        setMarkets([]);
      }
    } catch (err) {
      console.error('Error loading pending markets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pending markets');
      setMarkets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadApprovals = async (marketId: string) => {
    try {
      // Ensure marketId is a string for consistent key usage
      const marketIdStr = String(marketId);
      const response = await apiService.getMarketApprovals(marketIdStr);
      console.log(`Loaded approvals for market ${marketIdStr}:`, response);
      console.log(`Response type:`, typeof response);
      console.log(`Response.approvals:`, response.approvals);
      console.log(`Response.approvals type:`, typeof response.approvals);
      console.log(`Response.approvals length:`, response.approvals?.length);
      console.log(`Full response JSON:`, JSON.stringify(response, null, 2));
      
      if (response.approvals && response.approvals.length > 0) {
        console.log('First approval object:', response.approvals[0]);
        console.log('First approval keys:', Object.keys(response.approvals[0]));
        console.log('First approval JSON:', JSON.stringify(response.approvals[0], null, 2));
        console.log('First approval approved property:', response.approvals[0].approved);
        console.log('First approval approved type:', typeof response.approvals[0].approved);
      }
      
      const approvalsArray = response.approvals || [];
      setApprovals(prev => {
        const updated = { ...prev, [marketIdStr]: approvalsArray };
        console.log('Updated approvals state for', marketIdStr, ':', updated[marketIdStr]);
        if (updated[marketIdStr] && updated[marketIdStr].length > 0) {
          console.log('First approval in state:', updated[marketIdStr][0]);
          console.log('First approval in state keys:', Object.keys(updated[marketIdStr][0]));
          console.log('First approval in state JSON:', JSON.stringify(updated[marketIdStr][0], null, 2));
        }
        return updated;
      });
    } catch (err) {
      console.error('Failed to load approvals:', err);
    }
  };

  const handleApprove = async (marketId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (approving) return;
    
    const marketIdStr = String(marketId);
    setApproving(marketIdStr);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await apiService.approveMarket(marketIdStr);
      setSuccess(response.message || 'Market accepted and published');
      // Dismiss card immediately
      setMarkets(prev => prev.filter(m => m.id !== marketIdStr));
      setSelectedMarket(prev => (prev?.id === marketIdStr ? null : prev));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept market');
      setTimeout(() => setError(null), 5000);
    } finally {
      setApproving(null);
    }
  };

  const getAnswerOptions = (market: Market): string[] => {
    if (market.answer_type === 'yesno') {
      return ['Yes', 'No'];
    }
    if (market.answer_type === 'multiple' && market.answer_options) {
      return market.answer_options;
    }
    return [];
  };

  const formatTimeRemaining = (market: Market): string => {
    let endDate: Date | null = null;
    
    if (market.end_time_type === 'date' && market.end_date) {
      endDate = new Date(market.end_date);
    } else if (market.end_time_type === 'duration' && market.duration_value && market.duration_unit && market.created_at) {
      const createdDate = new Date(market.created_at);
      let milliseconds = 0;
      switch (market.duration_unit) {
        case 'Hours':
          milliseconds = market.duration_value * 60 * 60 * 1000;
          break;
        case 'Days':
          milliseconds = market.duration_value * 24 * 60 * 60 * 1000;
          break;
        case 'Weeks':
          milliseconds = market.duration_value * 7 * 24 * 60 * 60 * 1000;
          break;
        case 'Months':
          milliseconds = market.duration_value * 30 * 24 * 60 * 60 * 1000;
          break;
      }
      endDate = new Date(createdDate.getTime() + milliseconds);
    }
    
    if (!endDate) return 'N/A';
    
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    
    if (diff < 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d|${hours}h|${minutes}m`;
    if (hours > 0) return `${hours}h|${minutes}m`;
    return `${minutes}m`;
  };

  const getApprovalCount = (marketId: string): number => {
    // Ensure marketId is a string for consistent key usage
    const marketIdStr = String(marketId);
    const marketApprovals = approvals[marketIdStr] || [];
    if (marketApprovals.length > 0) {
      console.log(`Checking approvals for ${marketIdStr}:`, JSON.stringify(marketApprovals, null, 2));
      marketApprovals.forEach((a, idx) => {
        console.log(`Approval ${idx}:`, JSON.stringify(a), 'approved:', a.approved, 'type:', typeof a.approved, 'value:', a.approved);
        console.log(`Approval ${idx} approved === true:`, a.approved === true);
        console.log(`Approval ${idx} Boolean(approved):`, Boolean(a.approved));
      });
    }
    // Filter for approved approvals - check boolean true
    const count = marketApprovals.filter(a => {
      const isApproved = a.approved === true;
      console.log(`Approval check for ${a.admin_id || a.id}: approved=${a.approved}, type=${typeof a.approved}, isApproved=${isApproved}`);
      return isApproved;
    }).length;
    console.log(`Approval count for market ${marketIdStr}:`, count, 'from', marketApprovals.length, 'approvals');
    return count;
  };

  const getRequiredApprovals = (): number => (isSuperAdmin() ? 1 : 2);

  const isFullyApproved = (marketId: string): boolean => {
    return getApprovalCount(marketId) >= getRequiredApprovals();
  };

  const hasCurrentAdminApproved = (marketId: string): boolean => {
    if (!admin) return false;
    const marketIdStr = String(marketId);
    const marketApprovals = approvals[marketIdStr] || [];
    return marketApprovals.some(a => a.admin_id === admin.id && a.approved);
  };

  const isMarketCreator = (market: Market): boolean => {
    if (!admin) return false;
    return market.created_by === admin.id;
  };

  const isSuperAdmin = (): boolean => admin?.role === 'master';

  const canApprove = (market: Market): boolean => {
    if (!admin) return false;
    if (!isSuperAdmin() && isMarketCreator(market)) return false; // Creator cannot approve (super admin can bypass)
    if (hasCurrentAdminApproved(market.id)) return false; // Already approved
    if (isFullyApproved(market.id)) return false; // Fully approved
    return true;
  };

  const canReject = (): boolean => isSuperAdmin();

  const handleReject = async (marketId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!canReject() || rejecting) return;
    const marketIdStr = String(marketId);
    setRejecting(marketIdStr);
    setError(null);
    setSuccess(null);
    try {
      await apiService.rejectMarket(marketIdStr);
      setSuccess('Market rejected and returned to draft');
      // Dismiss card immediately
      setMarkets(prev => prev.filter(m => m.id !== marketIdStr));
      setSelectedMarket(prev => (prev?.id === marketIdStr ? null : prev));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject market');
      setTimeout(() => setError(null), 5000);
    } finally {
      setRejecting(null);
    }
  };

  return (
    <div className="pending-approval-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">⏳ Pending Approval</h1>
          <p className="page-sub">{markets.length} predictions awaiting second approval</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '24px' }}>
          {success}
        </div>
      )}

      <div className={`approval-warning ${isSuperAdmin() ? 'super-admin' : ''}`}>
        <span style={{ fontSize: '32px' }}>🔒</span>
        <div>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>
            {isSuperAdmin() ? 'Super Admin: Single Approval Publishes' : 'Dual Approval Required'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {isSuperAdmin()
              ? 'As super admin, your approval alone publishes the market. You can also reject to return to draft.'
              : 'All new markets require approval from 2 different admins before going live'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <p>Loading pending markets...</p>
        </div>
      ) : markets.length === 0 ? (
        <div className="empty-state">
          <p>No markets pending approval.</p>
        </div>
      ) : (
        <div className="prediction-cards">
          {markets.filter(market => market && market.id).map((market, index) => {
            const marketId = market.id;
            
            if (!marketId) {
              console.error('Market missing id at index:', index, market);
              return null;
            }
            
            const options = getAnswerOptions(market);
            const imageUrl = market.image_url || 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=400';
            const timer = formatTimeRemaining(market);
            const approvalCount = getApprovalCount(marketId);
            const fullyApproved = isFullyApproved(marketId);
            const marketApprovals = approvals[marketId] || [];
            
            return (
              <div 
                key={marketId} 
                className="prediction-card"
                onClick={() => setSelectedMarket(market)}
              >
                <div 
                  className="prediction-card-image"
                  style={{
                    backgroundImage: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%), url(${imageUrl})`
                  }}
                >
                  <div className="prediction-card-overlay">
                    <div className="prediction-timer">
                      {timer.split('|').map((t, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span>|</span>}
                          {t}
                        </React.Fragment>
                      ))}
                    </div>
                    <button 
                      className="prediction-share-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      📤
                    </button>
                  </div>
                  <div className="prediction-creator">
                    <div style={{
                      width: '36px',
                      height: '36px',
                      background: 'var(--purple)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: 'white'
                    }}>
                      {(market.created_by_name || market.created_by || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div className="prediction-creator-info">
                      <div className="prediction-creator-label">Created by</div>
                      <div className="prediction-creator-name">{market.created_by_name || market.created_by || 'Admin'}</div>
                    </div>
                  </div>
                </div>
                <div className="prediction-card-content">
                  <div className={`approval-status ${fullyApproved ? 'approved' : ''}`}>
                    <span style={{ fontSize: '20px' }}>{fullyApproved ? '✅' : '⏳'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>Approval Status: {approvalCount}/{getRequiredApprovals()}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {isSuperAdmin() ? 'Super admin: 1 approval publishes' : 'Requires 2 admin approvals'}
                      </div>
                    </div>
                  </div>
                  {(canApprove(market) || canReject()) && (
                    <div className="approval-actions" style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {canApprove(market) && (
                        <button
                          className="btn btn-success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(market.id, e);
                          }}
                          disabled={approving === market.id || rejecting === market.id}
                          style={{ backgroundColor: '#22c55e', color: 'white', border: 'none' }}
                        >
                          {approving === market.id ? 'Accepting...' : '✓ Accept'}
                        </button>
                      )}
                      {canReject() && (
                        <button
                          className="btn btn-danger"
                          onClick={(e) => handleReject(market.id, e)}
                          disabled={!!approving || !!rejecting}
                          style={{ backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                        >
                          {rejecting === market.id ? 'Rejecting...' : '✗ Reject'}
                        </button>
                      )}
                    </div>
                  )}
                  {marketApprovals.length > 0 && (
                    <div className="approval-by-section" style={{ marginBottom: '16px' }}>
                      {marketApprovals.map((approval, idx) => (
                        <div key={idx} className={`approval-by-item ${approval.approved ? '' : 'pending'}`}>
                          <div className="approval-icon">{approval.approved ? '✅' : '⏳'}</div>
                          <div className="approval-content">
                            <div className="approval-name">{approval.admin_name || 'Admin'}</div>
                            <div className="approval-meta">
                              {approval.approved ? 'Approved' : 'Pending'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="prediction-engagement">
                    <div className="engagement-item">❤️ 0</div>
                    <div className="engagement-item">💬 0</div>
                    <div className="engagement-item">📤 0</div>
                    <button className="share-earn-btn">Share & Earn</button>
                  </div>
                  <div className="prediction-title">{market.headline}</div>
                  {options.length > 0 && (
                    <div className="prediction-options">
                      {options.slice(0, 4).map((option, idx) => (
                        <div key={idx} className="prediction-option">{option}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedMarket && (
        <MarketDetailModal
          isOpen={!!selectedMarket}
          onClose={() => setSelectedMarket(null)}
          market={selectedMarket}
          onApprove={async () => {
            await loadApprovals(selectedMarket.id);
            await loadPendingMarkets();
          }}
          onReject={async () => {
            setSelectedMarket(null);
            await loadPendingMarkets();
          }}
        />
      )}
    </div>
  );
};

export default PendingApprovalPage;
